/**
 * Stripe subscription-status → Convex planStatus mapping and billing
 * display copy.
 *
 * `PlanStatus` mirrors the `users.planStatus` union in convex/schema.ts —
 * both sides change together. The mapping is TOTAL over Stripe's 8-value
 * status space; an unrecognized status (Stripe adding a value) returns an
 * Err so the webhook can warn + no-op instead of guessing.
 */

import { err, ok, type Result } from "../shared/result";
import type { Plan } from "../claw-config";

export type PlanStatus = "trialing" | "active" | "past_due" | "canceled";

export type UnknownStatusError = {
  readonly kind: "unknown_status";
  readonly status: string;
};

const STATUS_MAP: Record<string, PlanStatus> = {
  active: "active",
  trialing: "trialing",
  past_due: "past_due",
  canceled: "canceled",
  incomplete: "past_due",
  incomplete_expired: "canceled",
  unpaid: "past_due",
  paused: "canceled",
};

export function mapStripeSubscriptionStatus({
  status,
}: {
  status: string;
}): Result<PlanStatus, UnknownStatusError> {
  const mapped = STATUS_MAP[status];
  if (mapped === undefined) return err({ kind: "unknown_status", status });
  return ok(mapped);
}

export type StatusTone = "default" | "success" | "warning" | "error";

export const PLAN_STATUS_DISPLAY_LABEL: Record<
  PlanStatus,
  { readonly label: string; readonly tone: StatusTone }
> = {
  trialing: { label: "Pay as you go", tone: "default" },
  active: { label: "Active", tone: "success" },
  past_due: { label: "Payment past due", tone: "warning" },
  canceled: { label: "Canceled", tone: "error" },
};

export const PLAN_DISPLAY_LABEL: Record<Plan, string> = {
  trial: "Pay as you go",
  standard: "Standard",
  byok: "BYOK",
};
